import type { ID } from '@octopus-do/octopus-project-core'
import { LoaderActions } from '@store/loaders'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { call, put, select } from 'redux-saga/effects'

import { authSelector } from '@/hooks/useAuth'
import {
  fetchProjectStorageLimits,
  fetchTeamProjectStorageLimits,
  type ProjectStorageLimits,
} from '@/services/project'
import type { AuthState, StorageLimits, Team } from '@/types'
import { InternalStorageActions, type StorageActions } from '../actions'

// Хранилище команды бэк отдаёт только активному участнику активной команды,
// остальным — 401/403. В auth.teams лежат ровно такие команды: не нашли —
// значит гость или не-участник, и спрашиваем хранилище самого проекта.
const findAvailableTeam = (teams: Team[], teamId: ID | null): Team | null =>
  teams.find(({ id, isActive }) => id === teamId && isActive) ?? null

export function* setStorage({
  payload: { id },
}: ReturnType<typeof StorageActions.setStorage>) {
  const teamId: ID | null = yield select(
    migrationProjectSelector.getProjectTeamId
  )
  const { teams }: AuthState = yield select(authSelector)
  const team = findAvailableTeam(teams, teamId)

  try {
    yield put(LoaderActions.set('storage'))

    const project: ProjectStorageLimits = yield call(
      fetchProjectStorageLimits,
      id
    )
    const currentId: ID | null = yield select(
      migrationProjectSelector.getProjectId
    )

    if (currentId !== id) {
      return
    }

    yield put(
      InternalStorageActions.setQuota(
        project.quota ? { projectId: id, limits: project.quota } : null
      )
    )

    const limits: StorageLimits = team
      ? yield call(fetchTeamProjectStorageLimits, team.id)
      : project.limits

    const activeId: ID | null = yield select(
      migrationProjectSelector.getProjectId
    )

    if (activeId !== id) {
      return
    }

    yield put(InternalStorageActions.setStorage(limits))
  } catch (e) {
    // фоновая цифра хранилища — не повод показывать юзеру тост
    console.error(e)
  } finally {
    yield put(LoaderActions.unset('storage'))
  }
}
